import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useUserSettings } from '@/lib/user-settings-context';
import { useToast } from '@/hooks/use-toast';
import { useSpeech } from '@/hooks/use-speech';
import { roundToTwoDecimals, appendNote } from '@/lib/utils';
import { GoogleSheetSyncService } from '@/services/GoogleSheetSyncService';
import { UNIT_VALUE } from '@/lib/score';
import { useElapsedTime } from '@/hooks/use-elapsed-time';
import { useHaptics } from '@/hooks/use-haptics';
import confetti from 'canvas-confetti';
import { sounds } from '@/lib/sounds';
import { usePlayers, useCreatePlayer } from '@/hooks/use-players';
import { useSession, useCreateSession, useSessionTemplates, useCreateSessionTemplate, useDeleteSessionTemplate, useRoastMutation, useFinishSessionMutation } from '@/hooks/use-sessions';
import { useSessionResults, useAddBuyInMutation, useRemoveBuyInMutation, useUpdateCashOutMutation, useAddPlayerToSessionMutation, useUpdateSessionResults, useAddPlayersToSessionMutation, useRemovePlayerFromSessionMutation } from '@/hooks/use-session-players';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/hooks/query-keys';
import { supabase } from '@/integrations/supabase/client';
import type { NewSessionPlayerRow, SessionTemplate } from "@/ui/view-props";

const BUY_IN_UNIT_KEY = 'ssc_last_buy_in_unit';

export function useNewSession() {
  const { user } = useAuth();
  const { settings, updateSettings, loading: settingsLoading } = useUserSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const haptics = useHaptics();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const { data: players = [] } = usePlayers(user?.id);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const savedUnit = localStorage.getItem(BUY_IN_UNIT_KEY);
  const [buyInUnit, setBuyInUnit] = useState(savedUnit ? Number(savedUnit) : UNIT_VALUE);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const { data: session } = useSession(sessionId || undefined);
  const { data: sessionResults = [] } = useSessionResults(sessionId || undefined);
  const { data: templates = [] } = useSessionTemplates(user?.id);

  const createSessionMutation = useCreateSession();
  const createPlayerMutation = useCreatePlayer();
  const saveTemplateMutation = useCreateSessionTemplate();
  const deleteTemplateMutation = useDeleteSessionTemplate();
  const addBuyInMutation = useAddBuyInMutation();
  const removeBuyInMutation = useRemoveBuyInMutation();
  const updateCashOutMutation = useUpdateCashOutMutation();
  const addPlayerToSessionMutation = useAddPlayerToSessionMutation();
  const addPlayersToSessionMutation = useAddPlayersToSessionMutation();
  const removePlayerFromSessionMutation = useRemovePlayerFromSessionMutation();
  const finishSessionMutation = useFinishSessionMutation();
  const roastMutation = useRoastMutation();

  const [saving, setSaving] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isInitializingSession, setIsInitializingSession] = useState(true);
  const [roastGenerating, setRoastGenerating] = useState(false);
  const { isSpeaking: roastSpeaking, speak, stop: stopRoast, unlock: unlockSpeech } = useSpeech();
  const [roast, setRoast] = useState('');

  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [midSessionNewPlayerName, setMidSessionNewPlayerName] = useState('');
  const [confirmRemovePlayerId, setConfirmRemovePlayerId] = useState<string | null>(null);

  const sessionPlayers: NewSessionPlayerRow[] = useMemo(() => {
    return sessionResults.map((sp: any) => ({
      playerId: sp.player_id,
      name: sp.players?.name ?? 'Unknown',
      nickname: sp.players?.nickname ?? null,
      buyIns: sp.buy_ins ?? 1,
      cashOut: sp.cash_out?.toString() ?? '',
    }));
  }, [sessionResults]);

  useEffect(() => {
    if (!settings.realtime_session || !sessionId || step !== 2) return;

    const channel = supabase
      .channel(`session-${sessionId}`)
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'session_players', filter: `session_id=eq.${sessionId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [sessionId, step, settings.realtime_session, queryClient]);

  useEffect(() => {
    const rematch = searchParams.get('rematch');
    if (!rematch || players.length === 0) return;
    const ids = rematch.split(',');
    setSelectedIds(new Set(ids.filter((id) => players.some((p) => p.id === id))));
  }, [players, searchParams]);

  useEffect(() => {
    if (!user || settingsLoading) return;
    if (sessionId) {
      setIsInitializingSession(false);
      return;
    }

    if (settings.active_session_id) {
      setIsResuming(true);
      setSessionId(settings.active_session_id);
      setStep(2);
      setIsResuming(false);
      setIsInitializingSession(false);
    } else {
      setIsInitializingSession(false);
    }
  }, [user, settings.active_session_id, settingsLoading, sessionId]);

  const togglePlayer = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addPlayerInline = async () => {
    if (!user || !newPlayerName.trim()) return;
    createPlayerMutation.mutate({
      name: newPlayerName.trim(),
      created_by: user.id,
    }, {
      onSuccess: (data) => {
        setSelectedIds((prev) => new Set(prev).add(data.id));
        setNewPlayerName('');
        setShowAddPlayer(false);
      }
    });
  };

  const startSession = async () => {
    if (!user || selectedIds.size < 2) {
      toast({ variant: 'destructive', title: 'Select at least 2 players' });
      return;
    }

    createSessionMutation.mutate({
      created_by: user.id,
      buy_in_unit: buyInUnit,
    }, {
      onSuccess: async (session) => {
        setSessionId(session.id);
        await updateSettings({ active_session_id: session.id });

        addPlayersToSessionMutation.mutate({
          sessionId: session.id,
          playerIds: Array.from(selectedIds)
        }, {
          onSuccess: () => {
            setStep(2);
          }
        });
      }
    });
  };

  const addBuyIn = async (playerId: string) => {
    if (!sessionId) return;
    if (settings.extra_juice) {
      sounds.play('chip');
    }
    const playerResult = sessionResults.find(r => r.player_id === playerId);
    addBuyInMutation.mutate({
      sessionId,
      playerId,
      currentBuyIns: playerResult?.buy_ins || 0,
      buyInUnit
    });
  };

  const removeBuyIn = async (playerId: string) => {
    if (!sessionId) return;
    const playerResult = sessionResults.find(r => r.player_id === playerId);
    if (!playerResult || playerResult.buy_ins <= 1) return;
    
    removeBuyInMutation.mutate({
      sessionId,
      playerId,
      currentBuyIns: playerResult.buy_ins,
      buyInUnit
    });
  };

  const updateCashOut = async (playerId: string, cashOut: string) => {
    if (!sessionId) return;
    const numericCashOut = parseFloat(cashOut) || 0;
    updateCashOutMutation.mutate({
      sessionId,
      playerId,
      cashOut: numericCashOut,
      buyInUnit
    });
  };

  const addPlayerMidSession = async (player: any) => {
    if (!sessionId) return;
    addPlayerToSessionMutation.mutate({
      sessionId,
      playerId: player.id
    });
  };

  const removePlayerFromSession = async (playerId: string) => {
    if (!sessionId) return;
    const playerRow = sessionPlayers.find(sp => sp.playerId === playerId);
    if (playerRow && playerRow.buyIns > 1) {
      setConfirmRemovePlayerId(playerId);
      return;
    }
    removePlayerFromSessionMutation.mutate({ sessionId, playerId });
  };

  const confirmRemovePlayer = () => {
    if (!sessionId || !confirmRemovePlayerId) return;
    removePlayerFromSessionMutation.mutate({ sessionId, playerId: confirmRemovePlayerId });
    setConfirmRemovePlayerId(null);
  };

  const cancelRemovePlayer = () => setConfirmRemovePlayerId(null);

  const addNewPlayerMidSession = async (name: string) => {
    if (!user || !sessionId || !name.trim()) return;
    createPlayerMutation.mutate(
      { name: name.trim(), created_by: user.id },
      {
        onSuccess: (player) => {
          addPlayerToSessionMutation.mutate({ sessionId, playerId: player.id });
          setMidSessionNewPlayerName('');
        },
      }
    );
  };

  const onShareLiveLink = () => {
    if (!sessionId) return;
    const url = `${window.location.origin}/live/${sessionId}`;
    navigator.clipboard.writeText(url).then(() => {
      toast({ title: '🔗 Live link copied!', description: 'Share this URL with spectators to show them the live scoreboard.' });
    }).catch(() => {
      toast({ variant: 'destructive', title: 'Could not copy', description: url });
    });
  };

  const roastSession = async () => {
    if (roastSpeaking) {
      stopRoast();
      return;
    }

    if (roastGenerating || sessionPlayers.length === 0) return;

    unlockSpeech();
    setRoastGenerating(true);
    setRoast('');
    
    roastMutation.mutate(
      sessionPlayers.map((sp) => ({ name: sp.name, buyIns: sp.buyIns })),
      {
        onSuccess: (roastText) => {
          setRoast(roastText);
          toast({
            title: "AI Roast",
            description: roastText,
          });
          if (roastText) {
            speak(roastText);
          }
          setRoastGenerating(false);
        },
        onError: (err) => {
          console.error('Roast failed:', err);
          toast({ variant: 'destructive', title: 'Roast failed', description: err instanceof Error ? err.message : 'Unknown error' });
          setRoastGenerating(false);
        }
      }
    );
  };

  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [varianceAmount, setVarianceAmount] = useState(0);
  const [pendingVarianceNote, setPendingVarianceNote] = useState<string | null>(null);

  const attemptEndSession = () => {
    const totalBuyIns = sessionPlayers.reduce((s, p) => s + p.buyIns * buyInUnit, 0);
    const totalCashOuts = sessionPlayers.reduce((s, p) => s + (parseFloat(p.cashOut) || 0), 0);
    const diff = totalBuyIns - totalCashOuts;

    if (Math.abs(diff) < 0.01) {
      setShowSummaryModal(true);
      setPendingVarianceNote(null);
    } else {
      setVarianceAmount(diff);
      setShowVarianceModal(true);
    }
  };

  const handleVarianceResolved = (adjustedPlayers: NewSessionPlayerRow[], mode: 'split' | 'manual') => {
    // Generate variance note
    const note = mode === 'split' 
      ? `Variance of ${varianceAmount.toFixed(2)} split equally.` 
      : `Variance of ${varianceAmount.toFixed(2)} adjusted manually.`;
    setPendingVarianceNote(note);

    queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId || undefined), (old: any[] | undefined) => {
      if (!old) return [];
      return old.map(row => {
        const adjusted = adjustedPlayers.find(ap => ap.playerId === row.player_id);
        if (adjusted) {
          return { ...row, cash_out: parseFloat(adjusted.cashOut) || 0 };
        }
        return row;
      });
    });

    setShowVarianceModal(false);
    setShowSummaryModal(true);
  };

  const endSession = async () => {
    if (!sessionId) return;
    setSaving(true);

    const resultsToUpdate = sessionPlayers.map(sp => ({
      id: sp.playerId,
      buyIns: sp.buyIns,
      cashOut: roundToTwoDecimals(parseFloat(sp.cashOut) || 0),
      net: roundToTwoDecimals((parseFloat(sp.cashOut) || 0) - sp.buyIns * buyInUnit)
    }));

    const finalNotes = appendNote(session?.notes, pendingVarianceNote);

    finishSessionMutation.mutate({ sessionId, results: resultsToUpdate, notes: finalNotes }, {
      onSuccess: () => {
        if (settings.extra_juice) {
          confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
          haptics.success();
          sounds.play('chaching');
        }
        setShowSummaryModal(false);
        setSaving(false);
        navigate(`/session/${sessionId}`);
      },
      onError: () => {
        setSaving(false);
      }
    });
  };

  const availableMidSession = players.filter(
    (p) => !sessionPlayers.find((sp) => sp.playerId === p.id)
  );

  const elapsedTime = useElapsedTime(step === 2 ? session?.created_at : null);

  const handleSetBuyInUnit = (n: number) => {
    setBuyInUnit(n);
    localStorage.setItem(BUY_IN_UNIT_KEY, String(n));
  };

  const livePot = sessionPlayers.reduce((s, p) => s + p.buyIns * buyInUnit, 0);
  const liveBoard = [...sessionPlayers]
    .sort((a, b) => b.buyIns - a.buyIns)
    .map((p) => ({
      name: p.name,
      invested: p.buyIns * buyInUnit,
      pct: livePot > 0 ? (p.buyIns * buyInUnit) / livePot : 0,
    }));

  const handleSaveTemplate = async (name: string) => {
    if (!user || selectedIds.size < 2) return;
    saveTemplateMutation.mutate({
      name,
      player_ids: Array.from(selectedIds),
      created_by: user.id
    });
  };

  const handleLoadTemplate = (template: SessionTemplate) => {
    const validIds = template.player_ids.filter(id => players.some(p => p.id === id));
    setSelectedIds(new Set(validIds));
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!user) return;
    deleteTemplateMutation.mutate({ id, userId: user.id });
  };

  return {
    step,
    setStep,
    players,
    selectedIds,
    togglePlayer,
    showAddPlayer,
    setShowAddPlayer,
    newPlayerName,
    setNewPlayerName,
    addPlayerInline,
    startSession,
    sessionPlayers,
    addBuyIn,
    removeBuyIn,
    updateCashOut,
    availableMidSession,
    addPlayerMidSession,
    removePlayerFromSession,
    confirmRemovePlayerId,
    confirmRemovePlayer,
    cancelRemovePlayer,
    addNewPlayerMidSession,
    midSessionNewPlayerName,
    setMidSessionNewPlayerName,
    attemptEndSession,
    saving,
    showVarianceModal,
    setShowVarianceModal,
    varianceAmount,
    showSummaryModal,
    setShowSummaryModal,
    handleVarianceResolved,
    endSession,
    buyInAmount: buyInUnit,
    buyInUnit,
    setBuyInUnit: handleSetBuyInUnit,
    elapsedTime,
    isResuming,
    sessionId,
    showLiveScoreboard: settings.live_scoreboard,
    livePot,
    liveBoard,
    onShareLiveLink,
    onRoastSession: roastSession,
    roastGenerating,
    roastSpeaking,
    onStopRoast: stopRoast,
    roast,
    templates,
    onLoadTemplate: handleLoadTemplate,
    onSaveTemplate: handleSaveTemplate,
    onDeleteTemplate: handleDeleteTemplate,
    showTemplatesSection: settings.session_templates,
    settingsLoading,
    isInitializingSession
  };
}
